import React, { useCallback, useEffect, useState } from 'react';
import { Crown } from 'lucide-react';
import DataState from './DataState';
import LeaderboardFooter from './LeaderboardFooter';
import WalletLink from './WalletLink';

const apiUrl = process.env.REACT_APP_API_URL || '';

function Holders() {
  const [holders, setHolders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalItems: 0, totalPages: 0 });
  const [freshness, setFreshness] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchHolders = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ metric: 'holdings', page: String(page), pageSize: String(pageSize) });
      const response = await fetch(`${apiUrl}/api/v2/leaderboard?${params}`, { signal });
      if (!response.ok) throw new Error('Top holders could not be loaded.');
      const data = await response.json();
      setHolders(Array.isArray(data.data) ? data.data : []);
      setPagination(data.pagination || { page: 1, pageSize, totalItems: 0, totalPages: 0 });
      setFreshness(data.freshness || null);
    } catch (fetchError) {
      if (fetchError.name !== 'AbortError') {
        setError('The holder service is unavailable right now. The last published standings may still be syncing.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    const controller = new AbortController();
    fetchHolders(controller.signal);
    return () => controller.abort();
  }, [fetchHolders]);

  let state = null;
  if (loading && holders.length === 0) state = <DataState type="loading" title="Loading top holders" message="Counting the largest Pepe collections." />;
  else if (error && holders.length === 0) state = <DataState type="error" title="Holder rankings unavailable" message={error} actionLabel="Retry" onAction={() => fetchHolders()} />;
  else if (holders.length === 0) state = <DataState type="empty" title="No holder rankings published" message="Holder entries will appear after the next collection update." />;

  return (
    <div className="data-page">
      <section className="page-intro">
        <div className="content-width page-intro-inner">
          <div>
            <div className="eyebrow"><Crown size={17} aria-hidden="true" /> Collection leaders</div>
            <h1>Top holders</h1>
            <p>Meet the collectors holding the largest pieces of The Pepes supply.</p>
          </div>
          <div className="headline-stat">
            <strong>{loading && !pagination.totalItems ? '--' : pagination.totalItems.toLocaleString()}</strong>
            <span>Unique holders</span>
          </div>
        </div>
      </section>
      <section className="data-section content-width">
        <div className="data-tool holder-tool">
          <div className="data-tool-heading">
            <div><span>Collection standings</span><h2>Largest Pepe collections</h2></div>
            <span className="supply-note">Based on 10,000 originals</span>
          </div>
          {error && holders.length > 0 && (
            <div className="inline-data-warning" role="alert">
              <span>Showing the last loaded page. The requested update failed.</span>
              <button type="button" onClick={() => fetchHolders()}>Retry</button>
            </div>
          )}
          {state || (
            <>
              <div className="desktop-table-wrap">
                <table className="rankings-table">
                  <thead><tr><th>Rank</th><th>Wallet</th><th>Pepes owned</th><th>Supply</th></tr></thead>
                  <tbody>
                    {holders.map((holder) => (
                      <tr key={holder.walletId}>
                        <th scope="row"><span className="rank-badge">{holder.rank}</span></th>
                        <td><WalletLink address={holder.walletId} /></td>
                        <td className="number-cell">{Number(holder.ordAmount || 0).toLocaleString()}</td>
                        <td><span className="supply-badge">{(Number(holder.ordAmount || 0) / 100).toFixed(2)}%</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mobile-ranking-list">
                {holders.map((holder) => (
                  <article className="mobile-ranking-row" key={holder.walletId}>
                    <div className="mobile-rank">{holder.rank}</div>
                    <div className="mobile-wallet"><span>Wallet</span><WalletLink address={holder.walletId} /></div>
                    <div className="mobile-metric"><span>Owned</span><strong>{Number(holder.ordAmount || 0).toLocaleString()}</strong></div>
                    <div className="mobile-metric"><span>Supply</span><strong>{(Number(holder.ordAmount || 0) / 100).toFixed(2)}%</strong></div>
                  </article>
                ))}
              </div>
              <LeaderboardFooter
                freshness={freshness}
                pagination={pagination}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(nextSize) => { setPageSize(nextSize); setPage(1); }}
                disabled={loading}
              />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default Holders;
